import type { UserListItem, UsersListResponse } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import type { Key } from '@react-types/shared';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { ErrorHandler } from '@/utils/errorHandler';

interface FilterOwnerMultiSelectProps {
  label: string;
  value: string; // comma-separated user IDs
  onChange: (value: string) => void;
  className?: string;
}

function getUserLabel(user: UserListItem): string {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName ? `${fullName} (${user.email})` : user.email;
}

export function FilterOwnerMultiSelect({ label, value, onChange, className = 'w-64' }: FilterOwnerMultiSelectProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<UserListItem[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedUsers, setSelectedUsers] = React.useState<UserListItem[]>([]);

  // Parse comma-separated IDs into array
  const selectedUserIds = React.useMemo(() => {
    if (!value || value.trim() === '') {
      return [];
    }
    return value
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '')
      .map(Number);
  }, [value]);

  // Fetch users by IDs on mount and when value changes
  React.useEffect(() => {
    if (selectedUserIds.length === 0) {
      setSelectedUsers([]);
      return;
    }

    const fetchUsersByIds = async () => {
      try {
        const { data } = await apiClient.get('/users', {
          params: buildApiParams({ per_page: 100 }, {})
        });
        const response = data as UsersListResponse;
        const users = (response.data ?? []).filter((user) => selectedUserIds.includes(user.id));
        setSelectedUsers(users);
      } catch (error) {
        ErrorHandler.handle(error);
      }
    };

    void fetchUsersByIds();
  }, [selectedUserIds.join(',')]);

  const fetchUsers = React.useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await apiClient.get('/users', {
          params: buildApiParams({ per_page: 10 }, { search: term.trim() })
        });
        const response = data as UsersListResponse;
        // Filter out already selected users
        const filtered = (response.data ?? []).filter((user) => !selectedUserIds.includes(user.id));
        setSearchResults(filtered);
      } catch (error) {
        ErrorHandler.handle(error);
      } finally {
        setIsSearching(false);
      }
    },
    [selectedUserIds]
  );

  React.useEffect(() => {
    const handler = window.setTimeout(() => {
      void fetchUsers(searchTerm);
    }, 300);

    return () => {
      window.clearTimeout(handler);
    };
  }, [searchTerm, fetchUsers]);

  const handleUserAdd = React.useCallback(
    (key: Key | null) => {
      if (!key) return;

      const userId = String(key);
      const user =
        selectedUsers.find((u) => u.id.toString() === userId) || searchResults.find((u) => u.id.toString() === userId);

      if (!user) return;

      const newSelectedUsers = [...selectedUsers, user];
      setSelectedUsers(newSelectedUsers);

      // Update value with comma-separated IDs
      const newValue = newSelectedUsers.map((u) => u.id).join(',');
      onChange(newValue);

      setSearchTerm('');
    },
    [selectedUsers, searchResults, onChange]
  );

  const handleUserRemove = React.useCallback(
    (userId: number) => {
      const newSelectedUsers = selectedUsers.filter((user) => user.id !== userId);
      setSelectedUsers(newSelectedUsers);

      const newValue = newSelectedUsers.map((u) => u.id).join(',');
      onChange(newValue);
    },
    [selectedUsers, onChange]
  );

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="text-default-700 text-sm font-medium">{label}</label>
      <Autocomplete
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        selectedKey={null}
        onSelectionChange={handleUserAdd}
        isLoading={isSearching}
        placeholder={i18n.t('pages.users.searchPlaceholder')}
        size="sm"
        allowsCustomValue={false}
        onOpenChange={() => {}}
      >
        {searchResults.map((user) => (
          <AutocompleteItem key={user.id.toString()} textValue={getUserLabel(user)}>
            <div className="flex flex-col">
              <span className="font-medium">{getUserLabel(user)}</span>
              <span className="text-small text-default-500">{user.email}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {selectedUsers.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedUsers.map((user) => (
            <Chip key={user.id} size="sm" variant="flat" onClose={() => handleUserRemove(user.id)}>
              {getUserLabel(user)}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterOwnerMultiSelect;
